import {
  ServiceTypeFacetMode,
  ServiceTypeFacetModeSaveDTO,
  useCreateServiceTypeFacetModeMutation,
  useDeleteServiceTypeFacetModeMutation,
  useUpdateServiceTypeFacetModeMutation,
} from "@ots-frontend/ots-data";
import {useCallback, useEffect} from "react";
import {notifications} from "@mantine/notifications";
import {ResultTypeFrom} from "@reduxjs/toolkit/query";
import {useTranslation} from "react-i18next";
import {useDebouncedState} from "@mantine/hooks";
import {modals} from "@mantine/modals";
import {Text} from "@mantine/core";
import {EntityMutationCallback} from "./useEntityMutation";

export type UseServiceTypeFacetModeMutationProps = {}

export type UseServiceTypeFacetModeMutationResult = {
  createServiceTypeFacetMode: (serviceTypeFacetMode: ServiceTypeFacetModeSaveDTO, callback: EntityMutationCallback<ServiceTypeFacetMode> | undefined) => void
  updateServiceTypeFacetMode: (serviceTypeFacetMode: ServiceTypeFacetModeSaveDTO, callback: EntityMutationCallback<ServiceTypeFacetMode> | undefined) => void
  deleteServiceTypeFacetMode: (serviceTypeFacetModeId: string, callback: EntityMutationCallback | undefined) => void

  isCreating?: boolean
  isUpdating?: boolean
  isDeleting?: boolean
}

export const useServiceTypeFacetModeMutation = (props: UseServiceTypeFacetModeMutationProps = {}): UseServiceTypeFacetModeMutationResult => {
  const {t} = useTranslation(["common"])

  // state
  const [_creating, _setCreating] = useDebouncedState(false, 500)
  const [_updating, _setUpdating] = useDebouncedState(false, 500)
  const [_deleting, _setDeleting] = useDebouncedState(false, 500)

  // possible mutations
  const [_createServiceTypeFacetMode, {isLoading: isCreating}] = useCreateServiceTypeFacetModeMutation()
  const [_updateServiceTypeFacetMode, {isLoading: isUpdating}] = useUpdateServiceTypeFacetModeMutation()
  const [_deleteServiceTypeFacetMode, {isLoading: isDeleting}] = useDeleteServiceTypeFacetModeMutation()

  useEffect(() => {
    _setCreating(isCreating)
  }, [isCreating, _setCreating])

  useEffect(() => {
    _setUpdating(isUpdating)
  }, [isUpdating, _setUpdating])

  useEffect(() => {
    _setDeleting(isDeleting)
  }, [isDeleting, _setDeleting])

  useEffect(() => {
    if (_creating) {
      notifications.show({
        id: "useServiceTypeFacetModeUpdate_creating",
        title: t("common.status.creating", "Creating") + " " + t("common.entity.servicetypefacetmode", "Service Type Facet Mode"),
        message: "",
        loading: true,
        autoClose: 5000,
      });
    } else {
      notifications.hide("useServiceTypeFacetModeUpdate_creating")
    }
  }, [_creating, t]);

  useEffect(() => {
    if (_updating) {
      notifications.show({
        id: "useServiceTypeFacetModeUpdate_updating",
        title: t("common.status.updating", "Updating") + " " + t("common.entity.servicetypefacetmode", "Service Type Facet Mode"),
        message: "",
        loading: true,
        autoClose: 5000,
      });
    } else {
      notifications.hide("useServiceTypeFacetModeUpdate_updating")
    }
  }, [_updating, t]);

  useEffect(() => {
    if (_deleting) {
      notifications.show({
        id: "useServiceTypeFacetModeUpdate_deleting",
        title: t("common.status.deleting", "Deleting") + " " + t("common.entity.servicetypefacetmode", "Service Type Facet Mode"),
        message: "",
        loading: true,
        autoClose: 5000,
      });
    } else {
      notifications.hide("useServiceTypeFacetModeUpdate_deleting")
    }
  }, [_deleting, t]);

  const handleCreateResult = useCallback((promise: Promise<ServiceTypeFacetMode>, callback: EntityMutationCallback<ServiceTypeFacetMode> | undefined) => {
    promise
      .then((payload) => {
        notifications.show({
          color: "green",
          title: t("common.entity.servicetypefacetmode", "Service Type Facet Mode") + " " + t("common.status.created", "Created"),
          message: ""
        });
        if (callback?.onSuccess) {
          callback.onSuccess(payload)
        }
      })
      .catch((rejected) => {
          notifications.show({
            color: "red",
            title: t("common.error", "Error"),
            message: rejected.data?.detail ?? t("common.error", "Error"),
          })

          if (callback?.onFailure) {
            callback.onFailure()
          }
        }
      );
  }, [t])

  const handleUpdateResult = useCallback((promise: Promise<ResultTypeFrom<any>>, callback: EntityMutationCallback<ServiceTypeFacetMode> | undefined) => {
    promise
      .then(() => {
        notifications.show({
          color: "green",
          title: t("common.entity.servicetypefacetmode", "Service Type Facet Mode") + " " + t("common.status.updated", "Updated"),
          message: ""
        });
        if (callback?.onSuccess) {
          callback.onSuccess(undefined)
        }
      })
      .catch((rejected) => {
          notifications.show({
            color: "red",
            title: t("common.error", "Error"),
            message: rejected.data?.detail ?? t("common.error", "Error"),
          })

          if (callback?.onFailure) {
            callback.onFailure()
          }
        }
      );
  }, [t])

  const handleDeleteResult = useCallback((promise: Promise<ResultTypeFrom<any>>, callback: EntityMutationCallback | undefined) => {
    promise
      .then(() => {
        notifications.show({
          color: "green",
          title: t("common.entity.servicetypefacetmode", "Service Type Facet Mode") + " " + t("common.status.deleted", "Deleted"),
          message: ""
        });

        if (callback?.onSuccess) {
          callback.onSuccess(undefined)
        }
      })
      .catch((rejected) => {
          notifications.show({
            color: "red",
            title: t("common.error", "Error"),
            message: rejected.data?.detail ?? t("common.error", "Error"),
          })

          if (callback?.onFailure) {
            callback.onFailure()
          }
        }
      );
  }, [t])

  const handleDeleteConfirm = useCallback((serviceTypeFacetModeId: string, callback: EntityMutationCallback<ServiceTypeFacetMode> | undefined) => {
    const promise = _deleteServiceTypeFacetMode({id: serviceTypeFacetModeId}).unwrap()
    handleDeleteResult(promise, callback)
  }, [_deleteServiceTypeFacetMode, handleDeleteResult])

  const createServiceTypeFacetMode = useCallback((serviceTypeFacetMode: ServiceTypeFacetModeSaveDTO, callback: EntityMutationCallback<ServiceTypeFacetMode> | undefined) => {
    const promise = _createServiceTypeFacetMode(serviceTypeFacetMode).unwrap()
    handleCreateResult(promise, callback)
  }, [_createServiceTypeFacetMode, handleCreateResult])

  const updateServiceTypeFacetMode = useCallback((serviceTypeFacetMode: ServiceTypeFacetModeSaveDTO, callback: EntityMutationCallback | undefined) => {
    const promise = _updateServiceTypeFacetMode(serviceTypeFacetMode).unwrap()
    handleUpdateResult(promise, callback)
  }, [_updateServiceTypeFacetMode, handleUpdateResult])

  const deleteServiceTypeFacetMode = useCallback((serviceTypeFacetModeId: string, callback: EntityMutationCallback | undefined) => {
    modals.openConfirmModal({
      title: t("common.action.delete", "Delete") + " " + t("common.entity.servicetypefacetmode", "Service Type Facet Mode"),
      centered: true,
      children: (
        <Text size="sm" p="md">
          {t("common.action.delete.servicetypefacetmode.confirm", "Are you sure you want to delete this service type facet mode?")}
        </Text>
      ),
      labels: {
        confirm: t("common.action.delete", "Delete"),
        cancel: t("common.action.cancel", "Cancel"),
      },
      confirmProps: {color: 'red'},
      onConfirm: () => {
        handleDeleteConfirm(serviceTypeFacetModeId, callback)
      },
    })
  }, [handleDeleteConfirm, t])

  return {
    createServiceTypeFacetMode: createServiceTypeFacetMode,
    updateServiceTypeFacetMode: updateServiceTypeFacetMode,
    deleteServiceTypeFacetMode: deleteServiceTypeFacetMode,

    isCreating: _creating,
    isUpdating: _updating,
    isDeleting: _deleting,
  }
}
